// Resolve the signed-in user's email address (Unit 2.9a / #143).
//
// WHY THIS EXISTS: the Gen 2 Cognito pool is configured with email as the
// username attribute (`defineAuth({ loginWith: { email: true } })` ->
// `UsernameAttributes: ["email"]`). In that configuration Cognito assigns an
// opaque `sub` UUID as the internal username, so the Amplify v6
// `getCurrentUser()` / `useAuthenticator` `user.username` is that UUID, NOT
// the email. (Verified on the deployed dev bundle 2026-06-18: a recommender
// login rendered the greeting as `d8f1f390-...-...` -- the sub -- and
// TeacherView found no assigned applicants.)
//
// The recommender data path keys off the EMAIL: `Recommender` rows are keyed
// by email, and the `recommender*Email` / `schoolReportWriterEmail` columns on
// `ProdApplicant` are real email addresses written by ingestion. So
// `TeacherView`'s `Recommender.get({ email })` lookup and `Applicant.tsx`'s
// per-applicant email-match access check (`canAccessApplicant`) must use the
// email, not `user.username`. Before this helper they used `user.username`,
// which silently broke every recommender in the Gen 2 pool.
//
// `fetchUserAttributes()` is the canonical source for the email and works on
// both a fresh SRP sign-in and a session restored from storage. We fall back
// to `signInDetails.loginId` (populated right after an interactive sign-in)
// and finally to `user.username` (correct only on a username-based pool) so
// the helper degrades safely rather than throwing.
import { fetchUserAttributes } from "aws-amplify/auth";
import type { AuthUser } from "aws-amplify/auth";

export const resolveUserEmail = async (user: AuthUser): Promise<string> => {
  try {
    const attributes = await fetchUserAttributes();
    if (attributes.email) {
      return attributes.email;
    }
  } catch {
    // No readable session / attributes -- fall through to the synchronous
    // fallbacks below rather than blowing up the recommender view.
  }
  return user.signInDetails?.loginId ?? user.username;
};
